/**
 * Enrutado de la SPA y guard global de autenticación y permisos.
 *
 * ── Estructura ───────────────────────────────────────────────────
 *
 * Casi todas las rutas cuelgan del layout principal; `/Ingreso` (login) y
 * `/Notificacion` son las únicas públicas.
 *
 * Cada ruta declara en `meta`:
 *   `requierePermiso`  clave de permiso necesaria para navegar a ella
 *   `soloAdmin`        restringe a administradores
 *   `menu`             identificador del ítem de menú que debe marcarse activo
 *   `showNotificacion` si la vista muestra la campana de notificaciones
 *
 * ── El guard, paso a paso ────────────────────────────────────────
 *
 * 1. **`await authReady`** antes de cualquier decisión. Firebase resuelve el
 *    estado de sesión de forma asíncrona; sin esta espera, la primera navegación
 *    tras recargar la página vería `currentUser` en null y redirigiría al login
 *    a un usuario que sí tiene sesión. Es la causa del parpadeo de redirección
 *    que este `await` elimina.
 * 2. Refresca el ID token y lo inyecta en el cliente HTTP. Firebase lo renueva
 *    solo si está por vencer, así que llamarlo en cada navegación es barato.
 * 3. Si el store perdió su estado (recarga, despliegue nuevo) reverifica la
 *    sesión contra el backend con `restaurarSesion()`.
 * 4. Redirige según el resultado: los autenticados no entran a `/Ingreso`, los
 *    anónimos no entran a nada más.
 * 5. Filtra por `requierePermiso` y `soloAdmin`, enviando a `ListaVersiones`
 *    —no al login— cuando falta el permiso: el usuario está autenticado, solo no
 *    puede ver ESA pantalla.
 *
 * ⚠️ Este filtrado es de NAVEGACIÓN, no de seguridad. Evita mostrar pantallas
 * que producirían 403 y ocultar opciones inútiles; la autorización real la aplica
 * el backend en cada petición.
 *
 * ── Carga de componentes ─────────────────────────────────────────
 *
 * Todas las vistas se importan de forma estática (sin `() => import(...)`), así
 * que entran en el bundle inicial. Es una decisión con coste: el arranque carga
 * las ~40 vistas aunque el usuario solo use una.
 */

import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import PaginaPrincipal from '@/views/PaginaPrincipal.vue'
import GestionView from '@/views/GestionView.vue'
import AgregarComponentesView from '@/views/AgregarComponentesView.vue'
import LoginView from '@/views/LoginView.vue'
import VersionesView from '@/views/VersionesView.vue'
import CardNotificación from '@/components/CardNotificación.vue'
import ModeloDocumento from '@/components/ModeloDocumento.vue'
import MostrarEstructura from '@/components/MostrarEstructura.vue'
import GestionPesosView from '@/views/GestionPesosView.vue'
import AgregarIndicadoresView from '@/views/AgregarIndicadoresView.vue'
import InstrumentosView from '@/views/InstrumentosView.vue'
import FichaDocumental from '@/components/FichaDocumental.vue'
import FichaEstadistica from '@/components/FichaEstadistica.vue'
import FichaOpinion from '@/components/FichaOpinion.vue'
import GestionRubricasView from '@/views/GestionRubricasView.vue'
import CalificarInstrumentoOpinionView from '@/views/CalificarInstrumentoOpinionView.vue'
import ListaCalificar from '@/components/ListadoCalificar.vue'
import InstrumentoDetalle from '@/components/InstrumentoDetalle.vue'
import ModeloCompleto from '@/components/ModeloCompleto.vue'
import AsignacionRoles from '@/views/AsignacionRolesView.vue'
import EvidenciasInstrumentosView from '@/views/EvidenciasInstrumentosView.vue'
import AsignarEvaluadoresView from '@/views/AsignarEvaluadoresView.vue'
import EvaluacionIndividualView from '@/views/EvaluacionIndividualView.vue'
import ResultadosConsolidadosView from '@/views/ResultadosConsolidadosView.vue'
import ComparacionCalificacionesView from '@/views/ComparacionCalificacionesView.vue'
import ResultadosAutoevaluacionView from '@/views/ResultadosAutoevaluacionView.vue'
import ProcesoInstitucionalView from '@/views/ProcesoInstitucionalView.vue'
import InformeAutoevaluacionView from '@/views/InformeAutoevaluacionView.vue'
import PlanMejoramientoView from '@/views/PlanMejoramientoView.vue'
import AsignarTareasDirectorView from '@/views/AsignarTareasDirectorView.vue'
import AuditoriaView from '@/views/AuditoriaView.vue'
import EvaluacionExpertosView from '@/views/EvaluacionExpertosView.vue'
import ProcesoAutoevaluacionView from '@/views/ProcesoAutoevaluacionView.vue'
import ProcesoAutoevaluacionUsuarioView from '@/views/ProcesoAutoevaluacionUsuarioView.vue'
import AvanceProcesoView from '@/views/AvanceProcesoView.vue'
import DashboardView from '@/views/DashboardView.vue'
import SeguimientoPlanView from '@/views/SeguimientoPlanView.vue'
import CalendarioAcreditacionView from '@/views/CalendarioAcreditacionView.vue'
import TrazabilidadView from '@/views/TrazabilidadView.vue'
import { auth, authReady } from '@/firebase'
import { setAuthToken } from '@/services/http'
import useAutenticar from '@/store/IngresoAutenticar'
import GestionAreasView from '@/views/GestionAreasView.vue'
import TableroProgramasView from '@/views/TableroProgramasView.vue'

declare module 'vue-router' {
  interface RouteMeta {
    showNotificacion?: boolean
    requireAuth?: boolean
    menu?: string
    requierePermiso?: string
    soloAdmin?: boolean
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'primero',
    redirect: 'Ingreso'
  },
  {
    path: '/Gestion',
    name: 'Inicio',
    component: PaginaPrincipal,
    meta: {
      requireAuth: false,
      showNotificacion: true
    },
    children: [
        {
          path: '/Versiones',
          name: 'ListaVersiones',
          component: VersionesView,
          props: route => ({
            modoIndicadores: route.query.modo === 'indicadores',
            modoCalificar: route.query.modo === 'calificar instrumento'
          }),
          meta: { showNotificacion: true }
        },

        {
          path: 'Gestion',
          name: 'CrearModelo',
          component: GestionView,
          meta: {
            showNotificacion: true,
            menu: 'gestion-modelo',
            requierePermiso: 'modelos.crear'
           }
        },
        {
          path: '/version/:id_version/componentes/agregar',
          name: 'AgregarComponente',
          component: AgregarComponentesView,
          meta: {
            showNotificacion: false,
            menu: 'gestion-modelo',
            requierePermiso: 'componentes.crear'
          }
        },
        {
          path: '/modelo/:id_modelo/componentes',
          name: 'ComponentesModelo',
          component: AgregarComponentesView,
          meta: {
            showNotificacion: false,
            menu: 'gestion-modelo',
            requierePermiso: 'componentes.ver'
          }
        },
        {
          path: 'Documento/:id_modelo/:id_version',
          name: 'PresentarDocumento',
          component: ModeloDocumento,
          meta: {
            showNotificacion: false,
            menu: 'gestion-modelo',
            requierePermiso: 'modelos.ver'
          }
        },
        {
          path: 'ModeloCompleto/:id_modelo/:id_version',
          name: 'ModeloCompleto',
          component: ModeloCompleto,
          meta: {
            showNotificacion: false,
            menu: 'gestion-modelo',
            requierePermiso: 'modelos.ver'
          }
        },
        {
          path: 'Estructura/:id_modelo/:id_version?',
          name: 'EstructuraModelo',
          component: MostrarEstructura,
          meta: {
            showNotificacion: false,
            menu: 'gestion-modelo',
            requierePermiso: 'modelos.ver'
            }
        },
        {
          path: '/gestionPesos/:id_modelo',
          name: 'Pesos',
          component: GestionPesosView,
          meta: {
            showNotificacion: false,
            menu: 'gestion-modelo',
            requierePermiso: 'modelos.editar'
          }
        },

        {
          path: '/version/:id_version/indicadores',
          name: 'AgregarIndicadores',
          component: AgregarIndicadoresView,
          meta: {
            showNotificacion: false,
            menu: 'gestion-indicadores',
            requierePermiso: 'indicadores.crear'
          }
        },
        {
          path: '/Instrumento',
          name: 'Instrumento',
          component: InstrumentosView,
          props: route => ({
            idTipoComponente: Number(route.query.id_tipo_componente),
            idIndicador: Number(route.query.indicadorId),
            idInstrumento: route.query.id_instrumento ? Number(route.query.id_instrumento) : null
          }),
          meta: {
            showNotificacion: false,
            menu: 'gestion-indicadores',
            requierePermiso: 'instrumentos.editar'
          }
        },
        {
          path: '/ficha-documental',
          name: 'FichaDocumental',
          component: FichaDocumental,
          meta: {
            showNotificacion: false,
            menu: 'gestion-evidencias',
            requierePermiso: 'evidencias.subir'
          }
        },
        {
          path: '/ficha-estadistica',
          name: 'FichaEstadistica',
          component: FichaEstadistica,
          meta: {
            showNotificacion: false,
            menu: 'gestion-evidencias',
            requierePermiso: 'evidencias.subir'
          }
        },
        {
          path: '/ficha-opinion',
          name: 'FichaOpinion',
          component: FichaOpinion,
          meta: {
            showNotificacion: false,
            menu: 'gestion-evidencias',
            requierePermiso: 'evidencias.subir'
          }
        },
        {
          // Calificación de un instrumento de opinión pregunta por pregunta.
          path: '/instrumentos/:id_configuracion/calificar-opinion',
          name: 'CalificarInstrumentoOpinion',
          component: CalificarInstrumentoOpinionView,
          meta: {
            showNotificacion: false,
            menu: 'instrumentos',
            requierePermiso: 'calificacion.calificar'
          }
        },
        {
          path: '/rubricas',
          name: 'GestionRubricas',
          component: GestionRubricasView,
          meta: {
            showNotificacion: false,
            menu: 'gestion-rubricas',
            requierePermiso: 'rubricas.ver'
          }
        },
        {
          path: '/version/:id_version/calificar',
          name: 'ListaCalificar',
          component: ListaCalificar,
          meta: {
            showNotificacion: false,
            menu: 'gestion-instrumentos',
            requierePermiso: 'calificacion.calificar'
          }
        },
         {
          path: '/instrumento/:id',
          name: 'InstrumentoDetalle',
          component: InstrumentoDetalle,
          props: true,
          meta: {
            showNotificacion: false,
            menu: 'gestion-indicadores',
            requierePermiso: 'instrumentos.ver'
          }
        },

        {
          path: '/AsignarRoles',
          name: 'AsignarRoles',
          component: AsignacionRoles,
          props: true,
          meta: {
            showNotificacion: false,
            menu: 'gestion-usuarios',
            requierePermiso: 'usuarios.crear'
          }
        },

        {
          path: '/evidencias',
          name: 'SubirEvidencias',
          component: VersionesView,
          meta: {
            showNotificacion: false,
            menu: 'gestion-evidencias',
            requierePermiso: 'evidencias.subir'
          }
        },
        {
          path: '/evidencias/version/:id_version',
          name: 'EvidenciasInstrumentos',
          component: EvidenciasInstrumentosView,
          meta: {
            showNotificacion: false,
            menu: 'gestion-evidencias',
            requierePermiso: 'evidencias.subir'
          }
        },

        {
          path: '/version/:id_version/evaluadores',
          name: 'AsignarEvaluadores',
          component: AsignarEvaluadoresView,
          meta: {
            showNotificacion: false,
            menu: 'asignar-evaluadores',
            requierePermiso: 'procesos.asignar'
          }
        },
        {
          path: '/version/:id_version/indicador/:id_indicador/evaluar',
          name: 'EvaluacionIndividual',
          component: EvaluacionIndividualView,
          meta: {
            showNotificacion: false,
            menu: 'asignar-evaluadores',
            requierePermiso: 'calificacion.calificar'
          }
        },
        {
          path: '/version/:id_version/resultados',
          name: 'ResultadosConsolidados',
          component: ResultadosConsolidadosView,
          meta: {
            showNotificacion: false,
            menu: 'asignar-evaluadores',
            requierePermiso: 'calificacion.ver'
          }
        },
        {
          path: '/version/:id_version/auditoria',
          name: 'AuditoriaEvaluaciones',
          component: AuditoriaView,
          meta: {
            showNotificacion: false,
            menu: 'asignar-evaluadores',
            requierePermiso: 'calificacion.ver'
          }
        },
        {
          path: '/ejecucion/:id_ejecucion/comparacion',
          name: 'ComparacionCalificaciones',
          component: ComparacionCalificacionesView,
          meta: {
            showNotificacion: false,
            menu: 'proceso-autoevaluacion',
            requierePermiso: 'calificacion.ver'
          }
        },
        {
          path: '/ejecucion/:id_ejecucion/informe',
          name: 'InformeAutoevaluacion',
          component: InformeAutoevaluacionView,
          meta: {
            showNotificacion: false,
            menu: 'proceso-autoevaluacion',
            requierePermiso: 'informes.ver'
          }
        },
        {
          path: '/ejecucion/:id_ejecucion/plan-mejoramiento',
          name: 'PlanMejoramiento',
          component: PlanMejoramientoView,
          meta: {
            showNotificacion: false,
            menu: 'proceso-autoevaluacion',
            requierePermiso: 'planes.ver'
          }
        },
        {
          // Informe de Seguimiento al Plan de Mejoramiento / Fortalecimiento
          path: '/ejecucion/:id_ejecucion/seguimiento-plan',
          name: 'SeguimientoPlan',
          component: SeguimientoPlanView,
          meta: {
            showNotificacion: false,
            menu: 'proceso-autoevaluacion',
            requierePermiso: 'planes.ver'
          }
        },
        {
          // Autoevaluación institucional: agrupa una ejecución por campus y
          // consolida sus resultados.
          path: '/procesos-institucionales',
          name: 'ProcesosInstitucionales',
          component: ProcesoInstitucionalView,
          meta: {
            showNotificacion: false,
            menu: 'proceso-autoevaluacion',
            requierePermiso: 'procesos.asignar'
          }
        },
        {
          path: '/ejecucion/:id_ejecucion/resultados',
          name: 'ResultadosAutoevaluacion',
          component: ResultadosAutoevaluacionView,
          meta: {
            showNotificacion: false,
            menu: 'proceso-autoevaluacion',
            requierePermiso: 'calificacion.ver'
          }
        },
        {
          path: '/ejecucion/:id_ejecucion/asignar-tareas',
          name: 'AsignarTareasDirector',
          component: AsignarTareasDirectorView,
          meta: {
            showNotificacion: false,
            menu: 'proceso-autoevaluacion',
            requierePermiso: 'procesos.asignar'
          }
        },
        {
          path: '/version/:id_version/evaluacion-expertos',
          name: 'EvaluacionExpertos',
          component: EvaluacionExpertosView,
          meta: {
            showNotificacion: false,
            menu: 'evaluacion-expertos',
            requierePermiso: 'expertos.ver'
          }
        },

        {
          path: '/procesos-autoevaluacion',
          name: 'ProcesoAutoevaluacion',
          component: ProcesoAutoevaluacionView,
          meta: {
            showNotificacion: false,
            menu: 'proceso-autoevaluacion',
            requierePermiso: 'procesos.ver'
          }
        },
        {
          path: '/mis-procesos',
          name: 'ProcesoAutoevaluacionUsuario',
          component: ProcesoAutoevaluacionUsuarioView,
          meta: {
            showNotificacion: false,
            menu: 'proceso-autoevaluacion',
            requierePermiso: 'procesos.ver'
          }
        },
        {
          // RF-11.2 · Reporte de avance del proceso (dashboard F-04)
          path: '/ejecucion/:id_ejecucion/avance',
          name: 'AvanceProceso',
          component: AvanceProcesoView,
          meta: {
            showNotificacion: false,
            menu: 'proceso-autoevaluacion',
            requierePermiso: 'procesos.ver'
          }
        },
        {
          // RF-13 · Dashboard: centro de mando del proceso de acreditación
          path: '/dashboard',
          name: 'Dashboard',
          component: DashboardView,
          meta: {
            showNotificacion: false,
            menu: 'dashboard',
            requierePermiso: 'dashboard.ver'
          }
        },
        {
          // RF-13 · Calendario de acreditación (fechas regulatorias)
          path: '/calendario',
          name: 'Calendario',
          component: CalendarioAcreditacionView,
          meta: {
            showNotificacion: false,
            menu: 'dashboard',
            requierePermiso: 'calendario.ver'
          }
        },

        {
          // Tablero de la oferta académica: KPIs, matriz campus × nivel y
          // semáforo de vencimientos. Se alimenta del registro SNIES.
          path: '/tablero-programas',
          name: 'TableroProgramas',
          component: TableroProgramasView,
          meta: {
            showNotificacion: false,
            menu: 'tablero-programas',
            requierePermiso: 'campus.ver'
          }
        },
        {
          path: '/gestion-areas',
          name: 'GestionAreas',
          component: GestionAreasView,
          meta: {
            showNotificacion: false,
            menu: 'gestion-areas',
            requierePermiso: 'campus.ver'
          }
        },
        {
          path: '/trazabilidad',
          name: 'Trazabilidad',
          component: TrazabilidadView,
          meta: {
            showNotificacion: false,
            menu: 'trazabilidad',
            requierePermiso: 'trazabilidad.ver'
          }
        },
    ]
  },
  {
    path: '/Ingreso',
    name: 'Ingreso',
    component: LoginView,
    meta: {
      requireAuth: false
    }
  },
  {
    path: '/Notificacion',
    name: 'card',
    component: CardNotificación
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

const RUTAS_PUBLICAS = new Set(['Ingreso', 'card'])

router.beforeEach(async (to, _from, next) => {
  await authReady

  const firebaseUser = auth.currentUser
  const store = useAutenticar()

  if (firebaseUser) {
    // Refrescar token de Firebase (lo renueva automáticamente si está por vencer)
    const token = await firebaseUser.getIdToken()
    setAuthToken(token)

    // Si el store perdió estado (recarga, deploy), re-verificar contra el backend
    if (!store.autenticado) {
      await store.restaurarSesion()
    }
  }

  const autenticado = firebaseUser !== null && store.autenticado

  if (RUTAS_PUBLICAS.has(to.name as string)) {
    autenticado ? next({ name: 'Dashboard' }) : next()
    return
  }

  if (!autenticado) {
    next({ name: 'Ingreso' })
    return
  }

  // Verificar permiso de ruta
  const permiso = to.meta.requierePermiso
  if (permiso && !store.tienePermiso(permiso)) {
    next({ name: 'ListaVersiones' })
    return
  }

  // Verificar acceso solo-admin
  if (to.meta.soloAdmin && !store.esAdmin) {
    next({ name: 'ListaVersiones' })
    return
  }

  next()
})

export default router
